import {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    RawHTML,
} from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { Popover } from '@wordpress/components';
import { Store, ChevronLeft, ChevronDown } from 'lucide-react';
import {
    VendorAsyncSelect,
    DebouncedInput,
    DokanButton,
} from '@dokan/components';
import CustomerInfoModal from '../components/common/CustomerInfoModal';
import ShippingDetailsModal from '../components/common/ShippingDetailsModal';
import ProductsSection from '../components/single-quote/ProductsSection';
import CustomerInformation from '../components/single-quote/CustomerInformation';
import AdditionalMessage from '../components/single-quote/AdditionalMessage';
import StatusPill from '../components/quote-list/StatusPill';

type Customer = {
    id: number;
    name: string;
    email: string;
    phone: string;
    company: string;
    shipping?: {
        country: string;
        state: string;
        address_1: string;
        address_2: string;
        city: string;
        postcode: string;
    };
};

type ShippingDetails = {
    country: string; // country code
    state_name: string; // state code or name
    address_line_1: string;
    address_line_2: string;
    post_code: string;
    expected_delivery: string;
};

type Product = {
    id: number;
    name: string;
    image: string;
    permalink: string;
    price: number;
    quantity: number;
    offer_price: number;
};

const AddNewQuote = ( props: any ) => {
    const { id } = props.useParams();
    const [ quoteTitle, setQuoteTitle ] = useState( '' );
    const [ selectedVendor, setSelectedVendor ] = useState< any >( null );
    const [ selectedCustomer, setSelectedCustomer ] =
        useState< Customer | null >( null );
    const [ shippingDetails, setShippingDetails ] = useState< ShippingDetails >(
        {
            country: '',
            state_name: '',
            address_line_1: '',
            address_line_2: '',
            post_code: '',
            expected_delivery: '',
        }
    );
    const [ products, setProducts ] = useState< Product[] >( [] );
    const [ shippingCost, setShippingCost ] = useState( 0 );
    const [ isCustomerModalOpen, setIsCustomerModalOpen ] = useState( false );
    const [ isShippingModalOpen, setIsShippingModalOpen ] = useState( false );
    const [ isSubmitting, setIsSubmitting ] = useState( false );
    const [ isLoading, setIsLoading ] = useState( false );
    const [ errors, setErrors ] = useState< string[] >( [] );
    const [ quoteStatus, setQuoteStatus ] = useState( 'pending' );
    const [ quoteData, setQuoteData ] = useState< any >( null );
    const [ isDropdownOpen, setIsDropdownOpen ] = useState( false );
    const dropdownAnchorRef = useRef< HTMLButtonElement >( null );

    const isFormValid = useMemo( () => {
        if ( ! quoteTitle.trim() ) return false;
        if ( ! selectedVendor?.value ) return false;
        if ( selectedCustomer === null ) return false;
        if ( selectedCustomer.id === 0 ) {
            if ( ! selectedCustomer.name?.trim() ) return false;
            if ( ! selectedCustomer.email?.trim() ) return false;
        }
        if ( products.length === 0 ) return false;
        if ( products.some( ( p ) => p.quantity <= 0 || p.offer_price < 0 ) )
            return false;
        if ( shippingCost < 0 ) return false;
        return true;
    }, [
        quoteTitle,
        selectedVendor,
        selectedCustomer,
        products,
        shippingCost,
    ] );

    const productsData = {
        products,
        shipping_cost: shippingCost,
    };

    useEffect( () => {
        if ( id ) {
            const fetchQuote = async () => {
                setIsLoading( true );
                try {
                    const response: any = await apiFetch( {
                        path: `/dokan/v1/request-for-quote/${ id }`,
                    } );

                    setQuoteData( response );
                    setQuoteTitle( response.title || '' );
                    setQuoteStatus( response.status || 'pending' );
                    setShippingCost( Number( response.shipping_cost ) || 0 );

                    if ( response.store_info ) {
                        setSelectedVendor( {
                            value: response.store_info.store_id,
                            label: response.store_info.store_name,
                        } );
                    }

                    if ( response.customer && response.customer_info ) {
                        setSelectedCustomer( {
                            id: response.customer.user_id || 0,
                            name: response.customer_info.name_field || '',
                            email: response.customer_info.email_field || '',
                            phone: response.customer_info?.phone_field || '',
                            company:
                                response.customer_info?.company_field || '',
                        } );
                    }

                    if ( response.customer_info ) {
                        setShippingDetails( {
                            country: response.customer_info.country || '',
                            state_name:
                                response.customer_info.state_address || '',
                            address_line_1:
                                response.customer_info.addr_line_1 || '',
                            address_line_2:
                                response.customer_info.addr_line_2 || '',
                            post_code: response.customer_info.post_code || '',
                            expected_delivery: response.expected_date || '',
                        } );
                    }

                    if ( Array.isArray( response.products ) ) {
                        setProducts(
                            response.products.map( ( p: any ) => ( {
                                id: p.id,
                                name: p.name,
                                image: p.images?.[ 0 ]?.src || '',
                                permalink: p.permalink || '',
                                price: Number( p.price ) || 0,
                                quantity:
                                    Number( p.offer_product_quantity ) || 1,
                                offer_price: Number( p.offer_price ) || 0,
                            } ) )
                        );
                    }
                } catch ( e: any ) {
                    setErrors( [
                        e.message || __( 'Failed to fetch quote.', 'dokan' ),
                    ] );
                } finally {
                    setIsLoading( false );
                }
            };
            fetchQuote();
        }
    }, [ id ] );

    const isEditable = useMemo( () => {
        if ( ! id ) return true;
        return quoteStatus !== 'converted';
    }, [ id, quoteStatus ] );

    const shippingAddress =
        shippingDetails?.address_line_1 || __( 'None', 'dokan' );

    const handleProductsChange = useCallback( ( edits: any ) => {
        if ( edits.products !== undefined ) {
            setProducts( edits.products );
        }
        if ( edits.shipping_cost !== undefined ) {
            setShippingCost( edits.shipping_cost );
        }
    }, [] );

    const calculateSubtotal = useCallback( () => {
        return products.reduce( ( sum, product ) => {
            return sum + product.offer_price * product.quantity;
        }, 0 );
    }, [ products ] );

    const handleSaveCustomer = useCallback(
        ( customer: Customer ) => {
            setSelectedCustomer( customer );
            if ( customer.shipping ) {
                setShippingDetails( ( prev ) => ( {
                    ...prev,
                    country: customer.shipping!.country || '',
                    state_name: customer.shipping!.state || '',
                    address_line_1: customer.shipping!.address_1 || '',
                    address_line_2: customer.shipping!.address_2 || '',
                    post_code: customer.shipping!.postcode || '',
                } ) );
            }
            setIsCustomerModalOpen( false );
        },
        [ setShippingDetails ]
    );

    const handleSaveShipping = useCallback( ( shipping: ShippingDetails ) => {
        setShippingDetails( shipping );
        setIsShippingModalOpen( false );
    }, [] );

    const validate = () => {
        const errs: string[] = [];
        if ( ! quoteTitle.trim() )
            errs.push( __( 'Quote Title is required.', 'dokan' ) );
        if ( ! selectedVendor?.value )
            errs.push( __( 'Vendor is required.', 'dokan' ) );
        if ( selectedCustomer === null )
            errs.push( __( 'Customer is required.', 'dokan' ) );
        if ( products.length === 0 )
            errs.push( __( 'At least one product is required.', 'dokan' ) );
        products.forEach( ( p, i ) => {
            if ( p.quantity <= 0 )
                errs.push(
                    sprintf(
                        __(
                            'Quantity must be greater than 0 for row #%d',
                            'dokan'
                        ),
                        i + 1
                    )
                );
            if ( p.offer_price < 0 )
                errs.push(
                    sprintf(
                        __(
                            'Offer price cannot be negative for row #%d',
                            'dokan'
                        ),
                        i + 1
                    )
                );
        } );
        if ( selectedCustomer && selectedCustomer.id === 0 ) {
            if ( ! selectedCustomer.name?.trim() )
                errs.push( __( 'Customer full name is required.', 'dokan' ) );
            if ( ! selectedCustomer.email?.trim() )
                errs.push( __( 'Customer email is required.', 'dokan' ) );
        }
        if ( shippingCost < 0 )
            errs.push( __( 'Shipping cost cannot be negative.', 'dokan' ) );
        setErrors( errs );
        return errs.length === 0;
    };

    // Global expiry setting — used for pending quotes that haven't been approved yet.
    const hasGlobalExpiry = useMemo( () => {
        return quoteData?.enable_expiry_date && quoteData?.expiry_days > 0;
    }, [ quoteData ] );

    // Quote's own expiry date — set when the quote was approved while expiry was enabled.
    // This persists even if the global expiry setting is later disabled.
    const hasQuoteExpiry = useMemo( () => {
        return !! quoteData?.expiry_formatted_date;
    }, [ quoteData ] );

    const quoteNoticeClass = useMemo( () => {
        switch ( quoteStatus ) {
            case 'cancel':
                return 'bg-[#FFE5E5] border-[#E64B5F] text-[#393939]';
            case 'accepted':
                return 'bg-[#C0E1F8] border-[#1B6384] text-[#393939]';
            case 'updated':
                return 'bg-[#fed7aa] border-[#7c2d12] text-[#393939]';
            case 'reject':
                return 'bg-[#FFE5E5] border-[#E64B5F] text-[#393939]';
            case 'pending':
                return hasGlobalExpiry
                    ? 'bg-[#F4F1FE] border-[#7047EB] text-[#393939]'
                    : '';
            case 'approve':
                return hasQuoteExpiry
                    ? 'bg-[#FFF8E6] border-[#EFBB40] text-[#393939]'
                    : '';
            case 'expired':
                return hasQuoteExpiry
                    ? 'bg-[#FFE5E5] border-[#E64B5F] text-[#393939]'
                    : '';
            case 'converted':
                return quoteData?.converted_by
                    ? 'bg-[#E3FFF1] border-[#00BC8B] text-[#393939]'
                    : '';
            default:
                return '';
        }
    }, [ quoteStatus, quoteData, hasGlobalExpiry, hasQuoteExpiry ] );

    const quoteNoticeText = useMemo( () => {
        const {
            expiry_days,
            expiry_formatted_date,
            converted_by,
            show_converter,
        } = quoteData || {};

        switch ( quoteStatus ) {
            case 'cancel':
                return __( 'The Quote is Cancelled by the Customer.', 'dokan' );
            case 'accepted':
                return hasQuoteExpiry
                    ? sprintf(
                          __(
                              'The Quote is Accepted by the Customer. Please convert it before the quote expires on %s.',
                              'dokan'
                          ),
                          `<strong>${ expiry_formatted_date }</strong>`
                      )
                    : __( 'The Quote is Accepted by the Customer', 'dokan' );
            case 'updated':
                return __(
                    'The Quote has been Updated by the vendor. See below.',
                    'dokan'
                );
            case 'reject':
                return __(
                    'The Vendor has Rejected the Quotation. Send a New Quote.',
                    'dokan'
                );
            case 'pending':
                return hasGlobalExpiry
                    ? sprintf(
                          /* translators: 1) Expiry days, 2) Quote status content */
                          __(
                              'Please be Aware that the Quote Will Expire After %1$s from the %2$s.',
                              'dokan'
                          ),
                          `<strong>${
                              expiry_days === 1
                                  ? `${ expiry_days } day`
                                  : `${ expiry_days } days`
                          }</strong>`,
                          `<strong>${ __( 'Approval', 'dokan' ) }</strong>`
                      )
                    : '';
            case 'approve':
                return hasQuoteExpiry
                    ? sprintf(
                          /* translators: 1) Formatted expiry date */
                          __( 'The quote will expire on %1$s.', 'dokan' ),
                          `<strong>${ expiry_formatted_date }</strong>`
                      )
                    : '';
            case 'expired':
                return hasQuoteExpiry
                    ? sprintf(
                          /* translators: 1) Formatted expiry date */
                          __( 'The quote expired after %1$s.', 'dokan' ),
                          `<strong>${ expiry_formatted_date }</strong>`
                      )
                    : '';
            case 'converted':
                return converted_by
                    ? sprintf(
                          /* translators: 1) Display who converted */
                          __(
                              'The Quote Has been Converted to an Order%s.',
                              'dokan'
                          ),
                          show_converter
                              ? sprintf(
                                    /* translators: 1) Converted by */
                                    __( ' By %1$s', 'dokan' ),
                                    converted_by
                                )
                              : ''
                      )
                    : '';
            default:
                return '';
        }
    }, [ quoteStatus, quoteData, hasGlobalExpiry, hasQuoteExpiry ] );

    const quoteUpdated = useMemo( () => {
        if ( ! id || ! quoteData ) return false;
        const initialTotal = Array.isArray( quoteData.products )
            ? quoteData.products.reduce(
                  ( sum, p ) =>
                      sum +
                      Number( p.offer_price ) *
                          Number( p.offer_product_quantity ),
                  0
              ) + Number( quoteData.shipping_cost )
            : 0;
        const currentTotal = calculateSubtotal() + shippingCost;
        return initialTotal !== currentTotal && quoteStatus === 'pending';
    }, [
        id,
        quoteData,
        products,
        shippingCost,
        quoteStatus,
        calculateSubtotal,
    ] );

    const handleSaveQuote = useCallback(
        async ( status: string = 'pending' ) => {
            if ( ! validate() ) return;
            try {
                setIsSubmitting( true );
                const payload: any = {
                    quote_title: quoteTitle.trim(),
                    status,
                    user_id: selectedCustomer!.id,
                    store_info: {
                        store_id: selectedVendor!.value,
                        store_name: selectedVendor!.label,
                    },
                    customer_info: {
                        name_field: selectedCustomer!.name || '',
                        email_field: selectedCustomer!.email || '',
                        company_field: selectedCustomer!.company || '',
                        phone_field: selectedCustomer!.phone || '',
                        post_code: shippingDetails.post_code || '',
                        addr_line_1: shippingDetails.address_line_1 || '',
                        addr_line_2: shippingDetails.address_line_2 || '',
                        country: shippingDetails.country || '',
                        state_address: shippingDetails.state_name || '',
                    },
                    country: shippingDetails.country || '',
                    state_name: shippingDetails.state_name || '',
                    shipping_cost: Number( shippingCost ) || 0,
                    product_ids: products.map( ( p ) => p.id ),
                    offer_product_quantity: products.map( ( p ) => p.quantity ),
                    offer_price: products.map(
                        ( p ) => Number( p.offer_price ) || 0
                    ),
                    expected_delivery_date:
                        shippingDetails.expected_delivery || '',
                };

                const method = id ? 'PUT' : 'POST';
                const path = id
                    ? `dokan/v1/request-for-quote/${ id }`
                    : 'dokan/v1/request-for-quote';

                await apiFetch( {
                    path,
                    method,
                    data: payload,
                } );

                window.location.hash = '#/request-for-quote';
            } catch ( e: any ) {
                const msg =
                    e?.message ||
                    ( id
                        ? __( 'Failed to update quote.', 'dokan' )
                        : __( 'Failed to create quote.', 'dokan' ) );
                setErrors( ( prev ) => [ ...prev, msg ] );
            } finally {
                setIsSubmitting( false );
            }
        },
        [
            id,
            quoteTitle,
            selectedVendor,
            selectedCustomer,
            products,
            shippingDetails,
            shippingCost,
        ]
    );

    const handleConvertToOrder = useCallback( async () => {
        try {
            setIsSubmitting( true );
            await apiFetch( {
                path: 'dokan/v1/request-for-quote/convert-to-order',
                method: 'POST',
                data: {
                    quote_id: id,
                    status: 'converted',
                },
            } );
            window.location.hash = '#/request-for-quote';
        } catch ( e: any ) {
            setErrors( [
                e.message || __( 'Failed to convert to order.', 'dokan' ),
            ] );
        } finally {
            setIsSubmitting( false );
        }
    }, [ id ] );

    const handleRequote = useCallback( async () => {
        try {
            setIsSubmitting( true );
            await apiFetch( {
                path: `dokan/v1/request-for-quote/${ id }`,
                method: 'PUT',
                data: { status: 'pending' },
            } );
            window.location.reload();
        } catch ( e: any ) {
            setErrors( [ e.message || __( 'Failed to requote.', 'dokan' ) ] );
        } finally {
            setIsSubmitting( false );
        }
    }, [ id ] );

    return (
        <>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <a
                        href="#/request-for-quote"
                        className="flex items-center gap-1 text-sm text-[#828282] hover:text-[#7047EB] mb-2"
                    >
                        <ChevronLeft size={ 16 } />{ ' ' }
                        { __( 'Request for Quotation', 'dokan' ) }
                    </a>
                    <h1 className="text-2xl font-semibold pl-1 flex items-center gap-5">
                        { id
                            ? __( 'Editing Quote', 'dokan' )
                            : __( 'Add New Quote', 'dokan' ) }
                        { id && <StatusPill value={ quoteStatus } id={ id } /> }
                    </h1>
                </div>
                <div className="flex gap-2">
                    { /* New quote or draft: Create + Save as Draft dropdown */ }
                    { ( ! id || quoteStatus === 'draft' ) && (
                        <div className="relative inline-flex">
                            <DokanButton
                                type="primary"
                                className="px-5 py-2.5 rounded-md !rounded-r-none shadow-none"
                                onClick={ () => handleSaveQuote( 'pending' ) }
                                disabled={ isSubmitting || ! isFormValid }
                            >
                                { __( 'Create Quote', 'dokan' ) }
                            </DokanButton>
                            <hr className="h-full w-[1px] border-l border-white" />
                            <DokanButton
                                type="primary"
                                className="px-3 py-2.5 rounded-md !rounded-l-none shadow-none"
                                ref={ dropdownAnchorRef }
                                onClick={ () =>
                                    setIsDropdownOpen( ! isDropdownOpen )
                                }
                                disabled={ isSubmitting || ! isFormValid }
                            >
                                <ChevronDown size={ 16 } strokeWidth={ 3 } />
                            </DokanButton>
                            { isDropdownOpen && (
                                <Popover
                                    anchor={ dropdownAnchorRef.current }
                                    onClose={ () => setIsDropdownOpen( false ) }
                                    onFocusOutside={ () =>
                                        setIsDropdownOpen( false )
                                    }
                                    className="dokan-layout"
                                >
                                    <div className="header-popover bg-white rounded-md shadow-md min-w-[200px] transition-all duration-200 ease-in-out py-2">
                                        <ul className="flex flex-col p-0 m-0 list-none">
                                            <li className="m-0">
                                                <button
                                                    className="w-full rounded-none hover:bg-[#EFEAFF] text-[#828282] hover:text-[#7047EB] px-4 py-2 text-left"
                                                    onClick={ () => {
                                                        handleSaveQuote(
                                                            'draft'
                                                        );
                                                        setIsDropdownOpen(
                                                            false
                                                        );
                                                    } }
                                                >
                                                    { __(
                                                        'Save as Draft',
                                                        'dokan'
                                                    ) }
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </Popover>
                            ) }
                        </div>
                    ) }

                    { /* Pending: Approve/Update + Reject dropdown */ }
                    { id && quoteStatus === 'pending' && (
                        <div className="relative inline-flex">
                            <DokanButton
                                type="primary"
                                className="px-3 py-2.5 rounded-md !rounded-r-none ring-0"
                                onClick={ () =>
                                    handleSaveQuote(
                                        quoteUpdated ? 'updated' : 'approve'
                                    )
                                }
                                disabled={ isSubmitting || ! isFormValid }
                            >
                                { quoteUpdated
                                    ? __( 'Update Quote', 'dokan' )
                                    : __( 'Approve Quote', 'dokan' ) }
                            </DokanButton>
                            <hr className="h-full w-[1px] border-l border-white" />
                            <DokanButton
                                type="primary"
                                className="px-3 py-2.5 rounded-md !rounded-l-none ring-0"
                                ref={ dropdownAnchorRef }
                                onClick={ () =>
                                    setIsDropdownOpen( ! isDropdownOpen )
                                }
                                disabled={ isSubmitting || ! isFormValid }
                            >
                                <ChevronDown size={ 16 } />
                            </DokanButton>
                            { isDropdownOpen && (
                                <Popover
                                    anchor={ dropdownAnchorRef.current }
                                    onClose={ () => setIsDropdownOpen( false ) }
                                    onFocusOutside={ () =>
                                        setIsDropdownOpen( false )
                                    }
                                    className="dokan-layout"
                                >
                                    <div className="header-popover bg-white rounded-md shadow-md min-w-[200px] transition-all duration-200 ease-in-out py-2">
                                        <ul className="flex flex-col p-0 m-0 list-none">
                                            <li className="m-0">
                                                <button
                                                    className="w-full rounded-none hover:bg-[#EFEAFF] text-[#828282] hover:text-[#7047EB] px-4 py-2 text-left"
                                                    onClick={ () => {
                                                        handleSaveQuote(
                                                            'reject'
                                                        );
                                                        setIsDropdownOpen(
                                                            false
                                                        );
                                                    } }
                                                >
                                                    { __( 'Reject', 'dokan' ) }
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </Popover>
                            ) }
                        </div>
                    ) }

                    { /* Approve/Updated/Accepted: Convert to Order + Reject dropdown */ }
                    { id &&
                        [ 'approve', 'updated', 'accepted' ].includes(
                            quoteStatus
                        ) && (
                            <div className="relative inline-flex">
                                <DokanButton
                                    type="primary"
                                    className="px-5 py-2.5 rounded-md !rounded-r-none shadow-none"
                                    onClick={ handleConvertToOrder }
                                    disabled={ isSubmitting }
                                >
                                    { __( 'Convert to Order', 'dokan' ) }
                                </DokanButton>
                                <hr className="h-full w-[1px] border-l border-white" />
                                <DokanButton
                                    type="primary"
                                    className="px-3 py-2.5 rounded-md !rounded-l-none shadow-none"
                                    ref={ dropdownAnchorRef }
                                    onClick={ () =>
                                        setIsDropdownOpen( ! isDropdownOpen )
                                    }
                                    disabled={ isSubmitting }
                                >
                                    <ChevronDown
                                        size={ 16 }
                                        strokeWidth={ 3 }
                                    />
                                </DokanButton>
                                { isDropdownOpen && (
                                    <Popover
                                        anchor={ dropdownAnchorRef.current }
                                        onClose={ () =>
                                            setIsDropdownOpen( false )
                                        }
                                        onFocusOutside={ () =>
                                            setIsDropdownOpen( false )
                                        }
                                        className="dokan-layout"
                                    >
                                        <div className="header-popover bg-white rounded-md shadow-md min-w-[200px] transition-all duration-200 ease-in-out py-2">
                                            <ul className="flex flex-col p-0 m-0 list-none">
                                                <li className="m-0">
                                                    <button
                                                        className="w-full rounded-none hover:bg-[#EFEAFF] text-[#828282] hover:text-[#7047EB] px-4 py-2 text-left"
                                                        onClick={ () => {
                                                            handleSaveQuote(
                                                                'reject'
                                                            );
                                                            setIsDropdownOpen(
                                                                false
                                                            );
                                                        } }
                                                    >
                                                        { __(
                                                            'Reject',
                                                            'dokan'
                                                        ) }
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    </Popover>
                                ) }
                            </div>
                        ) }

                    { /* Rejected/Expired: Re-open */ }
                    { id && [ 'reject', 'expired' ].includes( quoteStatus ) && (
                        <DokanButton
                            type="primary"
                            className="px-5 py-2.5 rounded-md shadow-none"
                            onClick={ () => handleSaveQuote( 'pending' ) }
                            disabled={ isSubmitting }
                        >
                            { __( 'Re-open', 'dokan' ) }
                        </DokanButton>
                    ) }

                    { /* Cancelled/Trash: Reject + Re-open dropdown */ }
                    { id && [ 'cancel', 'trash' ].includes( quoteStatus ) && (
                        <div className="relative inline-flex">
                            <DokanButton
                                type="primary"
                                className="px-5 py-2.5 rounded-md !rounded-r-none shadow-none"
                                onClick={ () => handleSaveQuote( 'reject' ) }
                                disabled={ isSubmitting }
                            >
                                { __( 'Reject', 'dokan' ) }
                            </DokanButton>
                            <hr className="h-full w-[1px] border-l border-white" />
                            <DokanButton
                                type="primary"
                                className="px-3 py-2.5 rounded-md !rounded-l-none shadow-none"
                                ref={ dropdownAnchorRef }
                                onClick={ () =>
                                    setIsDropdownOpen( ! isDropdownOpen )
                                }
                                disabled={ isSubmitting }
                            >
                                <ChevronDown size={ 16 } strokeWidth={ 3 } />
                            </DokanButton>
                            { isDropdownOpen && (
                                <Popover
                                    anchor={ dropdownAnchorRef.current }
                                    onClose={ () => setIsDropdownOpen( false ) }
                                    onFocusOutside={ () =>
                                        setIsDropdownOpen( false )
                                    }
                                    className="dokan-layout"
                                >
                                    <div className="header-popover bg-white rounded-md shadow-md min-w-[200px] transition-all duration-200 ease-in-out py-2">
                                        <ul className="flex flex-col p-0 m-0 list-none">
                                            <li className="m-0">
                                                <button
                                                    className="w-full rounded-none hover:bg-[#EFEAFF] text-[#828282] hover:text-[#7047EB] px-4 py-2 text-left"
                                                    onClick={ () => {
                                                        handleSaveQuote(
                                                            'pending'
                                                        );
                                                        setIsDropdownOpen(
                                                            false
                                                        );
                                                    } }
                                                >
                                                    { __( 'Re-open', 'dokan' ) }
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </Popover>
                            ) }
                        </div>
                    ) }

                    { /* Converted: View Order */ }
                    { id &&
                        quoteStatus === 'converted' &&
                        quoteData?.order_url && (
                            <DokanButton
                                link={ true }
                                target="_blank"
                                className="px-5 py-2.5"
                                rel="noopener noreferrer"
                                href={ quoteData.order_url }
                            >
                                { __( 'View Order', 'dokan' ) }
                            </DokanButton>
                        ) }
                </div>
            </div>

            { quoteNoticeClass && quoteNoticeText && (
                <div
                    className={ `mb-6 p-4 rounded border !border-l-4 ${ quoteNoticeClass }` }
                >
                    <RawHTML>{ quoteNoticeText }</RawHTML>
                </div>
            ) }

            <div className="flex gap-6">
                { /* Left Column */ }
                <div className="flex-1">
                    { errors.length > 0 && (
                        <div className="mb-4 px-5 py-3 bg-red-50 border border-red-200 rounded-md text-[#E64B5F] leading-8">
                            <ul className="list-disc list-inside text-sm">
                                { errors.map( ( err, i ) => (
                                    <li className="m-0" key={ i }>
                                        { err }
                                    </li>
                                ) ) }
                            </ul>
                        </div>
                    ) }
                    <div className="bg-white p-7 rounded-lg shadow mb-6 space-y-5">
                        { /* Quote Title */ }
                        <div className="dokan-quote-title">
                            <label
                                htmlFor="dokan-rfq-quote-title"
                                className="block text-sm font-medium text-[#25252D] mb-2.5"
                            >
                                { __( 'Quote Title', 'dokan' ) }
                            </label>
                            <DebouncedInput
                                value={ quoteTitle }
                                className="focus:outline-0 !px-3 !rounded-lg"
                                onChange={ ( value ) => setQuoteTitle( value ) }
                                input={ {
                                    type: 'text',
                                    placeholder: __( 'Write here', 'dokan' ),
                                } }
                                disabled={ isSubmitting }
                            />
                        </div>

                        <div className="dokan-quote-vendor">
                            { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                            <label className="block text-sm font-medium text-[#25252D] mb-2.5">
                                { __( 'Select Vendor', 'dokan' ) }
                            </label>
                            <VendorAsyncSelect
                                value={ selectedVendor }
                                onChange={ ( e ) => {
                                    setSelectedVendor(
                                        e
                                            ? {
                                                  label: e.label,
                                                  value: e.value,
                                              }
                                            : null
                                    );
                                    setProducts( [] );
                                } }
                                placeholder={ __( 'Search Vendor', 'dokan' ) }
                                icon={ <Store size={ 16 } /> }
                                iconPosition="left"
                                isClearable
                                defaultOptions
                            />
                        </div>
                    </div>

                    { /* Products - Only show when vendor is selected */ }
                    <ProductsSection
                        products={ products }
                        isEditable={ isEditable }
                        productsData={ productsData }
                        selectedVendor={ selectedVendor }
                        handleProductsChange={ handleProductsChange }
                    />
                </div>

                { /* Right Column - Customer Information */ }
                <div className="w-80 space-y-6">
                    <CustomerInformation
                        selectedCustomer={ selectedCustomer }
                        setSelectedCustomer={ setSelectedCustomer }
                        setIsCustomerModalOpen={ setIsCustomerModalOpen }
                        setIsShippingModalOpen={ setIsShippingModalOpen }
                        shippingAddress={ shippingAddress }
                        shippingDetails={ shippingDetails }
                        setShippingDetails={ setShippingDetails }
                        quoteTitle={ quoteTitle }
                    />

                    { /* Additional Message Section */ }
                    <AdditionalMessage quoteData={ quoteData } />
                </div>
            </div>

            { /* Customer Info Modal */ }
            { isCustomerModalOpen && (
                <CustomerInfoModal
                    isOpen={ isCustomerModalOpen }
                    onClose={ () => setIsCustomerModalOpen( false ) }
                    onSave={ handleSaveCustomer }
                    customer={ selectedCustomer }
                />
            ) }

            { /* Shipping Details Modal */ }
            { isShippingModalOpen && (
                <ShippingDetailsModal
                    isOpen={ isShippingModalOpen }
                    onClose={ () => setIsShippingModalOpen( false ) }
                    onSave={ handleSaveShipping }
                    shippingDetails={ shippingDetails }
                />
            ) }
        </>
    );
};

export default AddNewQuote;
